// 从localStorage获取token
const getStoredToken = () => {
  return localStorage.getItem('token') || null;
};

// 从localStorage获取用户信息
const getStoredUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr || userStr === 'undefined' || userStr === 'null') {
      return null;
    }
    return JSON.parse(userStr);
  } catch (e) {
    console.error('Failed to parse user data:', e);
    return null;
  }
};

// 创建一个变量来存储 API 实例的 getter 函数
let getApiInstance = null;

// 提供设置 API 实例 getter 的方法
export function setApiGetter(getter) {
  getApiInstance = getter;
}

// 获取当前 API 实例的辅助函数
function getApi() {
  if (!getApiInstance) {
    throw new Error('API getter not set. Please initialize the auth module with an API instance.');
  }
  const api = getApiInstance();
  if (!api || !api.authApi) {
    throw new Error('Invalid API instance. Make sure the API object has authApi property.');
  }
  return api;
}

const state = {
  token: getStoredToken(),
  user: (() => {
    try {
      return getStoredUser();
    } catch (e) {
      console.error('Failed to initialize user state:', e);
      return null;
    }
  })(),
  preferences: {}, // 添加偏好设置状态
  menus: [], // 添加菜单状态（仅管理员端需要）
  dhDataMenus: [], // 添加数据中台菜单状态
  loading: false,
  error: null,
  wechatSignatures: {
    corp: null,
    agent: null
  },
  wechatUsers: [], // 添加企业微信用户列表状态
  isRefreshing: false,
  isDefaultPassword: false,
  adminUsers: [], // 管理员-用户列表
  adminUsersLoading: false // 管理员-用户列表加载状态
};

const getters = {
  isAuthenticated: state => !!state.token,
  currentUser: state => state.user,
  userMenus: state => state.menus, // 添加菜单getter（仅管理员端需要）
  dhDataMenus: state => state.dhDataMenus, // 添加数据中台菜单getter
  userPreferences: state => state.preferences, // 添加偏好设置getter
  authLoading: state => state.loading,
  authError: state => state.error,
  wechatUsers: state => state.wechatUsers,
  accessToken: state => state.token,
  isDefaultPassword: state => state.isDefaultPassword
};

const actions = {
  // 检查用户认证状态
  async checkAuth({ dispatch, state }) {
    // 如果已有token，尝试获取用户信息
    if (state.token) {
      try {
        await dispatch('fetchCurrentUser');
      } catch (error) {
        console.error('Session validation failed:', error);
        // 如果验证失败，清除认证状态
        // 但不重定向到登录页，让路由守卫处理
      }
    }
  },
  
  // 登录
   // 登录（更新以包含偏好设置）
  async login({ commit }, credentials) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const api = getApi();
      const response = await api.authApi.login(credentials);
      
      const { token, user, preferences, isDefaultPassword } = response.data;
      
      if (credentials.remember) {
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));
        localStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
        localStorage.setItem('isDefaultPassword', isDefaultPassword);  // ✅ 新增
      } else {
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('user', JSON.stringify(user));
        sessionStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
        sessionStorage.setItem('isDefaultPassword', isDefaultPassword);  // ✅ 新增
      }

      console.log('Login successful response.data:', response.data);
      
      commit('setAuth', {  token, user });
      commit('setPreferences', preferences || {}); // 设置偏好设置
      commit('setDefaultPassword', isDefaultPassword);  // ✅ 新增
      
      return { user, preferences };
    } catch (error) {
      commit('setError', (error.response && error.response.data && error.response.data.message) || '登录失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // ✅ 新增：刷新 token action
  async refreshToken({ commit, state, dispatch }) {
    // 防止重复刷新
    if (state.isRefreshing) {
      console.log('Token is already refreshing...');
      return false;
    }

    commit('setLoading', true);
    
    try {
      const api = getApi();
      
      // 使用 refresh token 请求新的 access token
      const response = await api.authApi.refreshToken();
      const { token } = response.data;
      
      // 更新 token
      commit('setToken', token);
      
      console.log('Token refreshed successfully');
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      
      // 刷新失败，清除所有认证信息
      commit('clearAuth');
      
      // 如果是 401，说明 refresh token 也过期了，需要重新登录
      if (error.response?.status === 401) {
        console.warn('Refresh token expired, please login again');
      }
      
      return false;
    } finally {
      commit('setLoading', false);
    }
  },

  async changePassword({ commit, state }, passwordData) {
    commit('setLoading', true)
    commit('clearError')
    
    try {
      // 获取 API 实例
      const api = getApi()
      
      // 调用 API 修改密码
      await api.authApi.changePassword(passwordData)
    
      // ✅ 修改密码成功后，更新默认密码标识为 false
      commit('setDefaultPassword', false);
      localStorage.setItem('isDefaultPassword', 'false');
      sessionStorage.setItem('isDefaultPassword', 'false');
      
      return true
    } catch (error) {
      const errorMsg = error.response?.data?.message || '修改密码失败'
      commit('setError', errorMsg)
      throw error
    } finally {
      commit('setLoading', false)
    }
  },

  // 微信登录（更新以包含偏好设置）
  async wechatLogin({ commit }, { code, state: wechatState }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const api = getApi();
      const response = await api.authApi.wechatLogin({ code, state: wechatState });
      
      const { token, user, preferences } = response.data;
      
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
      
      commit('setAuth', { token, user });
      commit('setPreferences', preferences || {}); // 设置偏好设置
      
      return { user, preferences };
    } catch (error) {
      console.error('微信登录错误:', error);
      commit('setError', error.response?.data?.message || error.message || '微信登录失败');
      commit('clearAuth');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('preferences'); // 清除偏好设置
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // 微信第三方应用登录（更新以包含偏好设置）
  async wechatLoginThird({ commit }, { code, state: wechatState }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const api = getApi();
      const response = await api.authApi.wechatLoginThird({ code, state: wechatState });
      
      const { token, user, preferences } = response.data;
      
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
      
      commit('setAuth', { token, user });
      commit('setPreferences', preferences || {}); // 设置偏好设置
      
      return { user, preferences };
    } catch (error) {
      console.error('微信登录错误:', error);
      commit('setError', error.response?.data?.message || error.message || '微信登录失败');
      commit('clearAuth');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('preferences'); // 清除偏好设置
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // 微信代开发应用登录（更新以包含偏好设置）
  async wechatLoginAgent({ commit }, { code, state: wechatState }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const api = getApi();
      const response = await api.authApi.wechatLoginAgent({ code, state: wechatState });
      
      const { token, user, preferences } = response.data;
      
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
      
      commit('setAuth', { token, user });
      commit('setPreferences', preferences || {}); // 设置偏好设置
      
      return { user, preferences };
    } catch (error) {
      console.error('微信登录错误:', error);
      commit('setError', error.response?.data?.message || error.message || '微信登录失败');
      commit('clearAuth');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('preferences'); // 清除偏好设置
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  async wechatClientLogin({ commit }, { code }) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      const api = getApi();
      const response = await api.authApi.wechatClientLogin({ code });
      
      const { token, user, preferences } = response.data;
      
      sessionStorage.setItem('token', token);
      sessionStorage.setItem('user', JSON.stringify(user));
      sessionStorage.setItem('preferences', JSON.stringify(preferences || {})); // 保存偏好设置
      
      commit('setAuth', { token, user });
      commit('setPreferences', preferences || {}); // 设置偏好设置
      
      return { user, preferences };
    } catch (error) {
      console.error('网页微信登录错误:', error);
      commit('setError', error.response?.data?.message || error.message || '微信登录失败');
      commit('clearAuth');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('preferences'); // 清除偏好设置
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 注册
  async register({ commit }, userData) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API注册
      const response = await api.authApi.register(userData);
      
      // 保存token和用户信息
      const { token, user } = response.data;
      
      // 存储到localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // 更新状态
      commit('setAuth', { token, user });
      
      return user;
    } catch (error) {
      commit('setError', (error.response && error.response.data && error.response.data.message) || '注册失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 登出
  async logout({ commit, rootGetters }) {
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API登出（如果需要）
      await api.authApi.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // 清除本地存储
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      
      // 清除状态
      commit('clearAuth');
      
      // 如果是管理员端，重定向到登录页
      if (rootGetters.isBackend) {
        // 重定向逻辑应该在组件或路由守卫中处理
      }
    }
  },
  
  // 获取当前用户信息（更新以包含偏好设置）
  async fetchCurrentUser({ commit, state }) {
    if (!state.token) return null;
    
    commit('setLoading', true);
    
    try {
      const api = getApi();
      const response = await api.authApi.getCurrentUser();
      const user = response.data;
      
      // 获取用户偏好设置
      let preferences = {};
      try {
        const prefResponse = await api.authApi.getUserPreferences();
        preferences = prefResponse.data.data || {};
      } catch (prefError) {
        console.warn('获取用户偏好设置失败:', prefError);
      }
      
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('preferences', JSON.stringify(preferences));
      
      commit('setUser', user);
      commit('setPreferences', preferences);
      
      return { user, preferences };
    } catch (error) {
      console.error('Fetch user error:', error);
      
      if (error.response && error.response.status === 401) {
        commit('clearAuth');
      }
      
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 获取用户偏好设置
  async fetchUserPreferences({ commit, state }) {
    if (!state.token) return {};
    
    try {
      const api = getApi();
      const response = await api.authApi.getUserPreferences();
      const preferences = response.data.data || {};
      
      localStorage.setItem('preferences', JSON.stringify(preferences));
      commit('setPreferences', preferences);
      
      return preferences;
    } catch (error) {
      console.error('获取用户偏好设置失败:', error);
      throw error;
    }
  },
  
  // 更新用户偏好设置
  async updateUserPreference({ commit, state }, { key, value }) {
    try {
      const api = getApi();
      await api.authApi.updateUserPreference(key, value);
      
      // 更新本地状态
      const newPreferences = { ...state.preferences, [key]: value };
      localStorage.setItem('preferences', JSON.stringify(newPreferences));
      commit('setPreferences', newPreferences);
      
      return newPreferences;
    } catch (error) {
      console.error('更新用户偏好设置失败:', error);
      throw error;
    }
  },
  
  // 获取当前用户菜单（仅管理员端需要）
  async fetchUserMenus({ commit, state }) {
    if (!state.token) return [];
    
    commit('setLoading', true);
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API获取用户菜单
      const response = await api.authApi.getUserMenus();
      const menus = response.data.data || response.data || [];
      
      // 更新状态
      commit('setMenus', menus);
      
      return menus;
    } catch (error) {
      console.error('Fetch menus error:', error);
      commit('setError', error.response?.data?.message || '获取菜单失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },

  // 确认用户的菜单是否已经加载
  async ensureMenusLoaded({ dispatch, state }) {
    // 如果菜单为空且已认证，则获取菜单
    if (state.token && (!state.menus || state.menus.length === 0)) {
      try {
        await dispatch('fetchUserMenus');
      } catch (error) {
        console.error('Failed to load menus for permission check', error);
        throw error;
      }
    }
  },
  
  // 获取当前用户的数据中台菜单（仅企业微信数据中台需要）
  async fetchDHDataUserMenus({ commit, state }) {
    if (!state.token) return [];
    
    commit('setLoading', true);
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API获取用户菜单
      const response = await api.authApi.getDHDataUserMenus();
      const dhDataMenus = response.data.data || response.data || [];
      
      // 更新状态
      commit('setDHDataMenus', dhDataMenus);
      
      return dhDataMenus;
    } catch (error) {
      console.error('Fetch DHData menus error:', error);
      commit('setError', error.response?.data?.message || '获取菜单失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 更新用户信息
  async updateUserProfile({ commit }, userData) {
    commit('setLoading', true);
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API更新用户信息
      const response = await api.authApi.updateProfile(userData);
      const updatedUser = response.data;
      
      // 更新本地存储
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // 更新状态
      commit('setUser', updatedUser);
      
      return updatedUser;
    } catch (error) {
      commit('setError', error.response?.data?.message || '更新个人资料失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 修改密码
  async changePassword({ commit }, passwordData) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API修改密码
      await api.authApi.changePassword(passwordData);
    } catch (error) {
      commit('setError', error.response?.data?.message || '修改密码失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 重置密码请求
  async requestPasswordReset({ commit }, email) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API请求重置密码
      await api.authApi.requestPasswordReset({ email });
    } catch (error) {
      commit('setError', error.response?.data?.message || '请求重置密码失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 重置密码
  async resetPassword({ commit }, resetData) {
    commit('setLoading', true);
    commit('clearError');
    
    try {
      // 获取 API 实例并获取当前用户
      const api = getApi();
      
      // 调用API重置密码
      await api.authApi.resetPassword(resetData);
    } catch (error) {
      commit('setError', error.response?.data?.message || '重置密码失败');
      throw error;
    } finally {
      commit('setLoading', false);
    }
  },
  
  // 验证token有效性
  async verifyToken({ dispatch, state }, token) {
    try {
      // 如果传入token与当前state不一致，先更新state
      if (token && token !== state.token) {
        // 这里不更新localStorage/sessionStorage，因为已经存在
        // 只需要更新Vuex state
        this.commit('auth/setAuth', { token, user: state.user });
      }

      // 检查token格式
      if (!token || typeof token !== 'string' || !token.includes('.')) {
        throw new Error('无效的token格式');
      }

      // 解码token payload部分
      const payloadBase64 = token.split('.')[1];
      try {
        const payload = JSON.parse(atob(payloadBase64));

        // 检查必要字段
        if (!payload.sub) {
          throw new Error('Token缺少subject字段');
        }

        // 检查过期时间
        if (payload.exp && Date.now() >= payload.exp * 1000) {
          throw new Error('Token已过期');
        }
      } catch (e) {
        throw e;
      }
      
      // 复用checkAuth逻辑验证token
      await dispatch('checkAuth');
      return true;
    } catch (error) {
      // 清除无效token
      localStorage.removeItem('token');
      sessionStorage.removeItem('token');
      this.commit('auth/clearAuth');
      
      return false;
    }
  },

  // 管理员功能：获取所有用户
  async fetchAllUsers({ commit }) {
    commit('setAdminUsersLoading', true);
    try {
      const api = getApi();
      const response = await api.authApi.getAllUsers();
      const users = response.data.users || [];
      commit('setAdminUsers', users);
      return users;
    } catch (error) {
      console.error('获取用户列表失败:', error);
      throw error;
    } finally {
      commit('setAdminUsersLoading', false);
    }
  },

  // 管理员功能：重置用户密码
  async resetUserPassword({ commit }, userId) {
    try {
      const api = getApi();
      await api.authApi.resetUserPassword(userId);
      return true;
    } catch (error) {
      console.error('重置用户密码失败:', error);
      throw error;
    }
  }
};

const mutations = {
  setAuth(state, { token, user }) {
    state.token = token;
    state.user = user;
  },
  
  // ✅ 新增：单独设置 token
  setToken(state, token) {
    state.token = token;
    localStorage.setItem('token', token);
    sessionStorage.setItem('token', token);
  },
  
  setUser(state, user) {
    state.user = user;
  },
  
  setPreferences(state, preferences) {
    state.preferences = preferences;
  },
  
  // 添加设置菜单的mutation（仅管理员端需要）
  setMenus(state, menus) {
    state.menus = menus;
  },
  
  // 获取当前用户的数据中台菜单（仅企业微信数据中台需要）
  setDHDataMenus(state, dhDataMenus) {
    state.dhDataMenus = dhDataMenus;
  },
  
  setDefaultPassword(state, isDefault) {  // ✅ 新增
    state.isDefaultPassword = isDefault;
  },
  
  clearAuth(state) {
    state.token = null;
    state.user = null;
    state.menus = []; // 清除菜单
    state.isDefaultPassword = false;  // ✅ 新增
  },
  
  setLoading(state, status) {
    state.loading = status;
  },
  
  setError(state, error) {
    state.error = error;
  },
  
  clearError(state) {
    state.error = null;
  },
  
  setWechatSignature(state, { type, signature }) {
    state.wechatSignatures[type] = signature;
  },
  
  // 修改设置企业微信用户信息的mutation
  setWechatUser(state, wechatUser) {
    // 将企业微信信息合并到用户对象中
    if (state.user) {
      state.user = { 
        ...state.user, 
        wechat_info: wechatUser,
        wechat_userid: wechatUser.userid // 专门存储企业微信用户ID
      };
    }
  },
  
  // 添加设置企业微信用户列表的mutation
  setWechatUsers(state, users) {
    state.wechatUsers = users;
  },

  // 管理员功能 mutations
  setAdminUsers(state, users) {
    state.adminUsers = users;
  },

  setAdminUsersLoading(state, loading) {
    state.adminUsersLoading = loading;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};